/** The activity record's Content actions (plan §3 "Actions", D5, O13, §9.6):
 *
 *  - **Duplicate…** — `POST /activities/{pk}/duplicate/` answers 202 with a background task
 *    (`console_activity_duplicate`, the `uploads` queue) that copies the activity, its tags,
 *    nearby activities, ratings, gallery images and documents; its result
 *    (`/bgtasks/{task_id}/result/`, ActivityDuplicateResult) names the copy and its console
 *    route. A second request while one runs is a 409 `duplicate_in_progress` naming that
 *    task. `console_activities_manage`; outside the catalog edit switch.
 *  - **Refresh from Google…** — `POST /activities/{pk}/google_place/lookup/` answers the
 *    activity's Google place as ready changes for the record's draft (`changes`, each `from`
 *    the edit_context baseline), with what Google agrees with (`unchanged`), what a field
 *    refuses (`skipped`) and what Google has nothing for (`missing`). It writes nothing: the
 *    draft's Save does. `console_activities_manage`, behind the catalog edit switch; 10 a
 *    minute per operator (429).
 *
 *  A refusal of either is ActivityRecordActionError (`detail`, `code`, …) — `actionError`. */
import * as bgtasksApi from './bgtasks';
import { apiPost, ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type ActivityDuplicateAccepted = Schemas['ActivityDuplicateAccepted'];
export type ActivityDuplicateResult = Schemas['ActivityDuplicateResult'];
export type ActivityRecordActionError = Schemas['ActivityRecordActionError'];
export type ActivityGooglePlaceLookup = Schemas['ActivityGooglePlaceLookup'];

/** The background task a duplicate runs as. */
export const ACTIVITY_DUPLICATE_TASK = 'console_activity_duplicate';

export function duplicateActivity(id: number, body: { name?: string } = {}): Promise<ActivityDuplicateAccepted> {
  return apiPost<ActivityDuplicateAccepted>(`/activities/${id}/duplicate/`, body);
}

/** A finished duplicate's result: the copy, and what was copied. */
export async function fetchDuplicateResult(taskId: string): Promise<ActivityDuplicateResult> {
  const r = await bgtasksApi.result(taskId);
  return r.result as unknown as ActivityDuplicateResult;
}

export function lookupGooglePlace(id: number): Promise<ActivityGooglePlaceLookup> {
  return apiPost<ActivityGooglePlaceLookup>(`/activities/${id}/google_place/lookup/`);
}

/** The refusal's body when the server sent one with a `code`; null otherwise. */
export function actionError(e: unknown): (ActivityRecordActionError & { status: number }) | null {
  if (!(e instanceof ConsoleApiError)) return null;
  const body: unknown = e.body;
  if (!body || typeof body !== 'object' || typeof (body as { code?: unknown }).code !== 'string') return null;
  return { ...(body as ActivityRecordActionError), status: e.status };
}
